import React, { FC, useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, ListItemIcon, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { User } from 'models/user';
import LinkedDentistsList from './linked-dentists-list';
import AddIcon from '@mui/icons-material/Add';
import { addLinkedDentistTemplate, getOrderForLinkedDentistTemplate } from 'apollo/templates/order';
import { useMutation } from '@apollo/client/react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import { getDentistsForLinked } from 'apollo/templates/user';
import { debounce } from 'lodash';
import { LinkedUser } from 'models/order';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { Organization } from 'models/organization';

type ManageLinkedDentistsProps = {
    orderId: string;
};
const ManageLinkedDentists: FC<ManageLinkedDentistsProps> = ({ orderId }: ManageLinkedDentistsProps) => {
    const { t: translate } = useTranslation('orders');
    const [dentists, setDentists] = useState<User[]>();
    const [notLinkedDentists, setNotLinkedDentists] = useState<User[]>();
    const [linkedDentists, setLinkedDentists] = useState<LinkedUser[]>();
    const [loading, setLoading] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [freeTextFilter, setFreeTextFilter] = useState('');
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(getDentistsForLinked, {
        variables: {
            filters: { freeText: freeTextFilter, active: true },
            clinicId: (currentUser.organization as Organization)?._id,
        },
        onCompleted: ({ dentists }) => {
            setDentists(dentists);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useQuery(getOrderForLinkedDentistTemplate, {
        variables: { orderId: orderId },
        onCompleted: ({ order }) => {
            setLinkedDentists(order.linkedDentists || []);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [addLinkedDentist] = useMutation(addLinkedDentistTemplate, {
        onCompleted: ({ addLinkedDentist }) => {
            // Updating the UI list (cache is already updated for order)
            if (linkedDentists) {
                const newDentist = addLinkedDentist.linkedDentists.slice(-1)[0];
                setLinkedDentists([...linkedDentists, newDentist]);
            }
            setLoading(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (dentists && linkedDentists) {
            const notLinked = dentists.filter((dentist) => {
                return !linkedDentists.find((linkedDentist) => {
                    return linkedDentist.user._id === dentist._id;
                });
            });
            setNotLinkedDentists(notLinked);
            setLoading(false);
        }
    }, [linkedDentists, dentists]);

    const addLinkedDentistUser = (dentist: User) => {
        setLoading(true);
        addLinkedDentist({ variables: { orderId: orderId, userId: dentist._id } });
    };

    const handleRemoveLinkedDentist = (dentist: User) => {
        if (linkedDentists) {
            setLinkedDentists(linkedDentists.filter((linkedDentist) => linkedDentist.user._id !== dentist._id));
        }
    };

    const handleAutocompleteInputChange = useCallback(
        debounce((value: string) => {
            setFreeTextFilter(value);
        }, 800),
        [inputValue],
    );

    return (
        <Box>
            <Box>
                {loading ? <DelayedLinearProgress /> : undefined}
                <Autocomplete
                    fullWidth
                    disableClearable
                    options={notLinkedDentists || []}
                    getOptionLabel={(dentist) => dentist.fullName}
                    renderOption={(props, dentist) => (
                        <MenuItem
                            {...props}
                            key={dentist._id}
                            value={dentist._id}
                            data-test={`manage-linked-dentists.add-option-${dentist._id}`}
                        >
                            <ListItemIcon>
                                <AddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                {dentist.fullName}
                                <Typography variant="body2" color="grey.700">
                                    {dentist.email}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={translate('ADD_PARTICIPANT')}
                            data-test="manage-linked-dentists.input"
                        />
                    )}
                    inputValue={inputValue}
                    onChange={(_, dentist) => {
                        addLinkedDentistUser(dentist);
                    }}
                    onInputChange={(_, value, reason) => {
                        if (reason === 'reset') {
                            setInputValue('');
                            return;
                        }
                        setInputValue(value);
                        handleAutocompleteInputChange(value);
                    }}
                />
            </Box>
            <Box mt={2}>
                {linkedDentists ? (
                    <LinkedDentistsList
                        orderId={orderId}
                        onRemove={handleRemoveLinkedDentist}
                        linkedDentists={linkedDentists}
                    />
                ) : undefined}
            </Box>
        </Box>
    );
};

export default ManageLinkedDentists;
